import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Globe, Loader2, Pencil, Trash2 } from 'lucide-react';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_BASE = (typeof window !== 'undefined' ? window.location.origin : '');

/**
 * Brand library picker — reuse artwork instead of re-uploading it.
 * Assets are immutable; applying a GLOBAL asset copies it into the org first,
 * which is what the `apply` call returns.
 */
export const BrandAssetPicker = ({ open, onOpenChange, kind, orgId, token, onApply, isSuperAdmin }) => {
    const { t } = useTranslation();
    const [scope, setScope] = useState('org');
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [renaming, setRenaming] = useState(null);
    const [draftName, setDraftName] = useState('');

    const headers = { Authorization: `Bearer ${token}` };

    const load = useCallback(async () => {
        if (!open || !kind) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({ kind, scope });
            if (scope === 'org' && orgId) params.set('organization_id', orgId);
            const res = await fetch(`${API_BASE}/api/brand-assets?${params}`, { headers });
            if (res.ok) setAssets((await res.json()).assets || []);
            else {
                setAssets([]);
                const body = await res.json().catch(() => ({}));
                toast.error(typeof body.detail === 'string' ? body.detail : t('brandLibrary.loadFailed'));
            }
        } catch { toast.error(t('brandLibrary.loadFailed')); }
        finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, kind, scope, orgId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (!open) { setScope('org'); setRenaming(null); } }, [open]);

    const use = async (asset) => {
        setBusyId(asset.id);
        try {
            const res = await fetch(`${API_BASE}/api/brand-assets/${asset.id}/apply`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ organization_id: orgId }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) { toast.error(typeof body.detail === 'string' ? body.detail : t('brandLibrary.applyFailed')); return; }
            onApply(body.url, body.asset);
            toast.success(body.copied ? t('brandLibrary.copiedToOrg') : t('brandLibrary.applied'));
            onOpenChange(false);
        } catch { toast.error(t('brandLibrary.applyFailed')); }
        finally { setBusyId(null); }
    };

    const rename = async (asset) => {
        const name = draftName.trim();
        if (!name) return;
        setBusyId(asset.id);
        try {
            const res = await fetch(`${API_BASE}/api/brand-assets/${asset.id}`, {
                method: 'PATCH',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ name }),
            });
            if (res.ok) { setRenaming(null); await load(); }
            else toast.error(t('brandLibrary.renameFailed'));
        } finally { setBusyId(null); }
    };

    const remove = async (asset) => {
        setBusyId(asset.id);
        try {
            const res = await fetch(`${API_BASE}/api/brand-assets/${asset.id}`, { method: 'DELETE', headers });
            if (res.ok) { toast.success(t('brandLibrary.deleted')); await load(); return; }
            const body = await res.json().catch(() => ({}));
            const usedBy = body?.detail?.used_by;
            if (Array.isArray(usedBy) && usedBy.length) {
                toast.error(`${t('brandLibrary.inUse')} ${usedBy.join(', ')}`, { duration: 8000 });
            } else {
                toast.error(typeof body.detail === 'string' ? body.detail : t('brandLibrary.deleteFailed'));
            }
        } finally { setBusyId(null); }
    };

    const promote = async (asset) => {
        setBusyId(asset.id);
        try {
            const res = await fetch(`${API_BASE}/api/brand-assets/${asset.id}/promote`, { method: 'POST', headers });
            const body = await res.json().catch(() => ({}));
            if (res.ok) toast.success(t('brandLibrary.promoted'));
            else toast.error(typeof body.detail === 'string' ? body.detail : t('brandLibrary.promoteFailed'));
        } finally { setBusyId(null); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl" data-testid="brand-library-dialog">
                <DialogHeader>
                    <DialogTitle>{t('brandLibrary.title')}</DialogTitle>
                    <DialogDescription>{t('brandLibrary.hint')}</DialogDescription>
                </DialogHeader>

                {isSuperAdmin && (
                    <div className="flex items-center gap-2" data-testid="brand-library-scope">
                        <Button
                            type="button" size="sm" variant={scope === 'org' ? 'default' : 'outline'}
                            onClick={() => setScope('org')} data-testid="brand-library-scope-org"
                        >
                            {t('brandLibrary.scopeOrg')}
                        </Button>
                        <Button
                            type="button" size="sm" variant={scope === 'global' ? 'default' : 'outline'}
                            onClick={() => setScope('global')} data-testid="brand-library-scope-global"
                        >
                            <Globe className="w-3 h-3 mr-1" /> {t('brandLibrary.scopeGlobal')}
                        </Button>
                    </div>
                )}

                {loading ? (
                    <div className="py-10 flex items-center justify-center text-muted-foreground" data-testid="brand-library-loading">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('brandLibrary.loading')}
                    </div>
                ) : assets.length === 0 ? (
                    <div className="py-10 text-center text-sm text-muted-foreground" data-testid="brand-library-empty">
                        {t('brandLibrary.empty')}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[55vh] overflow-y-auto pr-1" data-testid="brand-library-grid">
                        {assets.map((a) => (
                            <div key={a.id} className="rounded-lg border border-border bg-muted/20 p-2 space-y-2" data-testid={`brand-library-item-${a.id}`}>
                                <div className="h-24 rounded-md bg-slate-900 flex items-center justify-center overflow-hidden">
                                    <img src={`${API_BASE}${a.url}`} alt={a.name} className="max-h-full max-w-full object-contain" />
                                </div>
                                {renaming === a.id ? (
                                    <div className="flex items-center gap-1">
                                        <Input
                                            value={draftName} onChange={(e) => setDraftName(e.target.value)}
                                            className="h-7 text-xs" autoFocus
                                            data-testid={`brand-library-rename-input-${a.id}`}
                                        />
                                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => rename(a)}
                                            data-testid={`brand-library-rename-save-${a.id}`}>
                                            <Check className="w-3 h-3" />
                                        </Button>
                                    </div>
                                ) : (
                                    <p className="text-xs font-medium truncate" title={a.name}>{a.name}</p>
                                )}
                                <div className="flex items-center gap-1">
                                    <Button
                                        type="button" size="sm" className="h-7 px-2 text-xs flex-1"
                                        disabled={busyId === a.id} onClick={() => use(a)}
                                        data-testid={`brand-library-use-${a.id}`}
                                    >
                                        {busyId === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : t('brandLibrary.use')}
                                    </Button>
                                    <Button
                                        type="button" size="sm" variant="ghost" className="h-7 px-2"
                                        onClick={() => { setRenaming(a.id); setDraftName(a.name); }}
                                        data-testid={`brand-library-rename-${a.id}`}
                                    >
                                        <Pencil className="w-3 h-3" />
                                    </Button>
                                    <Button
                                        type="button" size="sm" variant="ghost"
                                        className="h-7 px-2 text-muted-foreground hover:text-red-500"
                                        onClick={() => remove(a)} data-testid={`brand-library-delete-${a.id}`}
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </Button>
                                </div>
                                {isSuperAdmin && scope === 'org' && (
                                    <Button
                                        type="button" size="sm" variant="outline"
                                        className="h-6 w-full text-[10px]"
                                        onClick={() => promote(a)} data-testid={`brand-library-promote-${a.id}`}
                                    >
                                        <Globe className="w-3 h-3 mr-1" /> {t('brandLibrary.promote')}
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
